import { useState, useEffect } from 'react';
import { getAdminMessages } from '../api';
import type { Message } from '../types';
import { AdminMessageCard } from './AdminMessageCard';

interface Props { secret: string; }

export function AdminView({ secret }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    getAdminMessages(secret)
      .then(setMessages)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [secret]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d1a] flex items-center justify-center">
        <p className="text-slate-500">Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0d0d1a] flex items-center justify-center">
        <p className="text-red-400">Access denied: {error}</p>
      </div>
    );
  }

  const pending = messages.filter(m => !m.activated && !m.deleted_at);
  const active = messages.filter(m => m.activated && !m.deleted_at);
  const deleted = messages.filter(m => !!m.deleted_at);

  return (
    <div className="min-h-screen bg-[#0d0d1a] text-slate-200">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-violet-400">Admin</h1>
          <div className="flex gap-4 text-sm">
            <span className="text-slate-500">{pending.length} pending</span>
            <span className="text-emerald-600">{active.length} active</span>
            <span className="text-red-800">{deleted.length} deleted</span>
          </div>
        </div>

        {pending.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
              Pending Activation
            </h2>
            {pending.map(m => (
              <AdminMessageCard key={m.id} message={m} secret={secret} onAction={load} />
            ))}
          </section>
        )}

        {active.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
              Active
            </h2>
            {active.map(m => (
              <AdminMessageCard key={m.id} message={m} secret={secret} onAction={load} />
            ))}
          </section>
        )}

        {deleted.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
              Deleted
            </h2>
            {deleted.map(m => (
              <AdminMessageCard key={m.id} message={m} secret={secret} onAction={load} />
            ))}
          </section>
        )}

        {messages.length === 0 && (
          <p className="text-center text-slate-600 py-16">No messages yet.</p>
        )}
      </div>
    </div>
  );
}
